import { useAuth0 } from '@auth0/auth0-react'
import { Box, Chip, Typography } from '@mui/material'
import {
  IconAdjustments,
  IconBuildingStore,
  IconToolsKitchen2,
} from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import EmptyState from '../../../components/EmptyScreen'
import Filter from '../../../components/filter/Filter'
import { getMatchQualityCategory } from '../../../helpers/restaurant-helpers'
import ResactlyTab from '../../../components/ResactlyTab'
import RoundedButton from '../../../components/ui/RoundedButton'
import {
  convertToPredictedAndPublicRestaurantList,
  generateEater,
} from '../../../helpers/converters'
import { getLocation } from '../../../helpers/location-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import { price } from '../../../helpers/static-constants'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../store'
import { selectLocation } from '../../../store/authSlice'
import {
  getLandingPageCityRestaurantDetails,
  getLandingPageCuisineDetails,
  selectRestaurants,
  setRestaurants as setLandingRestaurants,
} from '../../../store/landingSlice'
import { selectEater } from '../../../store/profileSlice'
import { selectRadius } from '../../../store/restaurantSlice'
import { selectSearchId, setSearchId } from '../../../store/searchSlice'
import {
  Dish,
  IFilterResponseItem,
  IFilterTabList,
  IRestaurantsCuisineRequest,
  ITabList,
  Restaurant,
  RestaurantAndPredictedRestaurant,
} from '../../../types'
import { fetchGlobalSearch } from '../../../services/searchService'
import { logEvent } from '../../../analytics/analytics'
import useRudderStackAnalytics from '../../../useRudderAnalytics'
import SearchDish from './components/SearchDish'
import SearchResultRestaurants from './components/SearchResultRestaurants'
import {
  SearchResultsFilterButton,
  SearchResultsHeaderContainer,
  SearchResultsHeaderLeftContainer,
  SearchResultsHeaderRightContainer,
  SearchResultsHeaderSearchTermTypography,
  SearchResultsMainContainer,
  SearchResultsResactlyTabContainer,
} from './styled/SearchResults.styled'

const newSearchId = (): string => {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID()
    }
  } catch {
    // fall through
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// Zero-input discovery nudge for the search entry state: tapping a cuisine runs a real cuisine
// search instead of leaving the user staring at a blank prompt. Only ~2% of users ever search, so
// the entry state should INVITE a query. Curated seed for now — swap for live popular cuisines later.
const POPULAR_CUISINES = [
  'Italian',
  'Mexican',
  'Chinese',
  'Indian',
  'Japanese',
  'Thai',
  'Mediterranean',
  'American',
]

const SearchResults: React.FC = () => {
  const {
    theme,
    navigate,
    location: urlLocation,
    dispatch,
    isMediumScreen,
  } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const queryParams = new URLSearchParams(urlLocation.search)
  const query = queryParams.get('q')
  const type = queryParams.get('type')
  const eater = useAppSelector(selectEater)
  const radius = useAppSelector(selectRadius)
  const locationData = useAppSelector(selectLocation)
  const restaurantsDetail = useAppSelector(selectRestaurants)
  const searchId = useAppSelector(selectSearchId)
  const [dishes, setDishes] = useState<Dish[]>([])
  const [pagTitle, setPageTitle] = React.useState('')
  const [tabList, setTabList] = useState<ITabList[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [filterModalOpen, setFilterModalOpen] = React.useState(false)
  const [filterTabList, setFilterTabList] = useState<IFilterTabList[]>([])
  const [restaurants, setRestaurants] = useState<
    RestaurantAndPredictedRestaurant[]
  >([])
  const [filterRestaurants, setFilterRestaurants] = useState<
    RestaurantAndPredictedRestaurant[]
  >([])
  // Phase 2 — free-text results are now server-paginated via the resactly-service /search proxy
  // (no more Redux snapshot replay). These track paging for the "Load more" control.
  const SEARCH_PAGE_SIZE = 20
  const [searchPage, setSearchPage] = useState(0)
  const [searchTotal, setSearchTotal] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  // True when the backend widened a cuisine/city browse to the nearest-available because the user's
  // local area had no coverage — surfaces a "showing the nearest" note instead of a silent blank.
  const [broadened, setBroadened] = useState(false)

  useEffect(() => {
    setPageTitle(
      `Resactly | Find Your Next Favorite Restaurant${
        type === 'cuisine' && query
          ? ` With ${query} Cuisine`
          : type === 'city' && query
          ? `With ${formatQueryName(query)} city`
          : ''
      }`,
    )
  }, [])

  useEffect(() => {
    const { fullName, eaterId, email } = generateEater(eater ?? {})

    if (analytics && isAnalyticsReady) {
      analytics?.page('PAGE_OPENED', {
        path: ROUTES.SEARCH,
        title: pagTitle,
        url: `${window.location.origin}${ROUTES.SEARCH}`,
        category: 'Search',
        name: 'Search Viewed',
        eaterName: fullName,
        email,
        eaterId,
        startTime: new Date().toISOString(),
      })
    }
  }, [analytics, pagTitle, eater, isAnalyticsReady])

  useEffect(() => {
    const { fullName, eaterId, email } = generateEater(eater ?? {})

    return () => {
      analytics?.page('PAGE_CLOSED', {
        path: ROUTES.SEARCH,
        title: pagTitle,
        url: `${window.location.origin}${ROUTES.SEARCH}`,
        category: 'Search',
        name: 'Search Viewed',
        eaterName: fullName,
        email,
        eaterId,
        endTime: new Date().toISOString(),
      })
    }
  }, [location])

  // Ensure a non-empty search_id exists for this results view. When the search originated in
  // GlobalSearch the session id is already in Redux; on a direct link/reload it is missing, so mint
  // one here. Guarantees search_result_selected can never fire with an empty search_id.
  useEffect(() => {
    if (query && !searchId) {
      dispatch(setSearchId(newSearchId()))
    }
  }, [query, searchId, dispatch])

  useEffect(() => {
    const tabs = [
      {
        label: 'Restaurant',
        value: 'restaurant',
        icon: <IconBuildingStore stroke={2} />,
      },
    ]

    if (type === 'restaurant' && tabList.length < 3) {
      tabs.push({
        label: 'Dish',
        value: 'dish',
        icon: <IconToolsKitchen2 stroke={2} />,
      })
    }

    setTabList(tabs)
    void fetchData()
  }, [radius, type, query])

  useEffect(() => {
    // cuisine/city browse still uses the geo-aware landing thunks (Redux). The restaurant
    // (free-text) tab is populated server-side by loadSearchResults, not a snapshot.
    if (type === 'cuisine' || type === 'city') {
      setRestaurants(restaurantsDetail ?? [])
    }
  }, [restaurantsDetail, type])

  useEffect(() => {
    if (restaurants && restaurants.length) {
      const filter = onFilterItems()
      setFilterTabList(filter)
    }
  }, [restaurants])

  // search_performed for the results page / deep-link / cuisine-city browse. Fires once per
  // (searchId, type, query) after results resolve (searchTerm is only set once loaded), reusing the
  // session searchId so its search_result_selected picks join back to this query. Deduped so
  // "Load more" and re-renders don't re-emit; a new query mints a fresh event.
  const searchPerformedKeyRef = React.useRef<string>('')
  useEffect(() => {
    if (!query || !searchId || !searchTerm) return
    const key = `${searchId}::${type ?? ''}::${query}`
    if (searchPerformedKeyRef.current === key) return
    searchPerformedKeyRef.current = key
    logEvent('search_performed', {
      search_id: searchId,
      term: query,
      result_count: type === 'restaurant' ? searchTotal : restaurants.length,
      screen_name: 'search_results',
    })
  }, [query, type, searchId, searchTerm, searchTotal, restaurants.length])

  // Query the resactly-service /search proxy (which forwards to resactly-dis + Elasticsearch) for
  // real, ranked, paginated free-text results. Splits the ranked, mixed-entity page into the
  // Restaurant and Dish tabs. `append` powers "Load more"; page 0 replaces.
  const loadSearchResults = async (
    pageToLoad: number,
    append: boolean,
    loc?: { latitude?: number; longitude?: number },
  ): Promise<void> => {
    if (!query) return
    setLoadingMore(true)
    try {
      const result = await fetchGlobalSearch({
        q: query,
        lat: loc?.latitude,
        lng: loc?.longitude,
        page: pageToLoad,
        size: SEARCH_PAGE_SIZE,
      })

      const restaurantSources = result.items
        .filter((item) => item.type === 'restaurant')
        .map((item) => item.source as unknown as Restaurant)
      const dishSources = result.items
        .filter((item) => item.type === 'dish')
        .map((item) => item.source as unknown as Dish)
      const mappedRestaurants =
        convertToPredictedAndPublicRestaurantList(restaurantSources)

      setSearchTotal(result.total)
      setSearchPage(result.page)
      if (append) {
        setRestaurants((prev) => [...prev, ...mappedRestaurants])
        setDishes((prev) => [...prev, ...dishSources])
      } else {
        setRestaurants(mappedRestaurants)
        setDishes(dishSources)
      }
      setSearchTerm(query)
    } finally {
      setLoadingMore(false)
    }
  }

  const hasMoreSearchResults =
    type === 'restaurant' &&
    (searchPage + 1) * SEARCH_PAGE_SIZE < searchTotal

  const fetchData = async (): Promise<void> => {
    setBroadened(false)
    let local = locationData
    if (locationData === undefined) {
      local = await getLocation()
    }

    if (type === 'restaurant') {
      await loadSearchResults(0, false, local)
    }

    if (type === 'cuisine' && query) {
      const payload: IRestaurantsCuisineRequest = {
        longitude: local?.longitude ?? -73.935242,
        latitude: local?.latitude ?? 40.73061,
        radius,
        name: query,
        page: 0,
      }

      const result = await dispatch(getLandingPageCuisineDetails(payload))
      if (getLandingPageCuisineDetails.fulfilled.match(result)) {
        setBroadened(
          Boolean((result.payload as { broadened?: boolean } | undefined)?.broadened),
        )
        dispatch(
          setLandingRestaurants({ response: result.payload, isAuthenticated }),
        )
      }
      setSearchTerm(query + ' cuisine Restaurants')
    }
    if (type === 'city' && query) {
      const payload: IRestaurantsCuisineRequest = {
        longitude: local?.longitude ?? -73.935242,
        latitude: local?.latitude ?? 40.73061,
        radius,
        name: query,
        page: 0,
      }

      const result = await dispatch(
        getLandingPageCityRestaurantDetails(payload),
      )
      if (getLandingPageCityRestaurantDetails.fulfilled.match(result)) {
        setBroadened(
          Boolean((result.payload as { broadened?: boolean } | undefined)?.broadened),
        )
        dispatch(
          setLandingRestaurants({ response: result.payload, isAuthenticated }),
        )
      }

      setSearchTerm(formatQueryName(query) + ' city Restaurants')
    }
  }

  const onFilter = (obj: IFilterResponseItem): void => {
    if (restaurants) {
      const data = restaurants.filter((dish) => {
        if (
          obj.price !== undefined &&
          obj.price > 1 &&
          price.indexOf(dish?.restaurantDTO?.priceRange?? '0') > Number(obj.price) - 1
        ) {
          return false
        }
        if (
          obj.distance !== undefined &&
          obj.distance > 0 &&
          Number(dish?.restaurantDTO?.distance ?? 0) > Number(obj.distance)
        ) {
          return false
        }

        if (obj.dietary && obj.dietary.length > 0) {
          const dietaryValues = obj.dietary.map((item) =>
            item.name.trim().toLowerCase(),
          )
          if (
            !dietaryValues.some((dietary) =>
              dish?.restaurantDTO?.dietary
                ?.map((d) => d.trim().toLowerCase())
                .includes(dietary.trim().toLowerCase()),
            )
          ) {
            return false
          }
        }
        if (obj.cuisine && obj.cuisine.length > 0) {
          const cuisineValues = obj.cuisine.map((item) => item.name)
          if (
            !cuisineValues.some(
              (dietary) =>
                dish?.restaurantDTO?.cuisine?.toLocaleLowerCase() ===
                dietary.toLowerCase(),
            )
          ) {
            return false
          }
        }

        if (obj.matchquality && obj.matchquality.length > 0) {
          const matchQualityValues = obj.matchquality.map((item) => item.value)
          const predictionPercentage = dish.recommendation?.yes

          if (typeof predictionPercentage !== 'number') {
            return false
          }

          const matchCategory = getMatchQualityCategory(predictionPercentage)

          if (!matchQualityValues.includes(matchCategory)) {
            return false
          }
        }

        return true
      })
      setFilterRestaurants(data)
    }
  }

  const onFilterItems = (): IFilterTabList[] => {
    const filterItems = [
      {
        key: 1,
        name: 'Distance',
        items: [
          { key: 1, name: '0 mi', value: '0' },
          { key: 2, name: '5 mi', value: '5' },
          { key: 3, name: '10 mi', value: '10' },
          { key: 4, name: '15 mi', value: '15' },
          { key: 5, name: '20 mi', value: '20' },
          { key: 6, name: '25 mi', value: '25' },
          { key: 7, name: '30 mi', value: '30' },
        ],
        rangeStep: 5,
        filterType: 'range',
      },
      {
        key: 8,
        name: 'Match Quality',
        items: [
          { key: 1, name: 'Perfect Match', value: 'perfectmatch' },
          { key: 2, name: 'Great Match', value: 'greatmatch' },
          { key: 3, name: 'Ok Match', value: 'okmatch' },
          { key: 4, name: 'Not so great', value: 'notsogreat' },
          { key: 5, name: 'Low Match', value: 'lowmatch' },
        ],
        filterType: 'checkbox',
      },
    ].filter(Boolean) as IFilterTabList[]

    if (restaurants && restaurants.length !== 0) {
      const allDietaryPreferences = restaurants.flatMap(
        (item) =>
          item?.restaurantDTO?.dietary?.map((preference) =>
            preference.trim(),
          ) ?? [],
      )

      const uniqueDietaryPreferences = Array.from(
        new Set(
          allDietaryPreferences.filter(
            (preference): preference is string =>
              preference !== undefined && preference.trim() !== '',
          ),
        ),
      )

      const cuisineList: string[] = restaurants
        .flatMap(
          (item) =>
            item?.restaurantDTO?.cuisine
              ?.split(',')
              .map((cuisine) => cuisine.trim()) ?? [],
        )
        .filter((cuisine): cuisine is string => cuisine !== undefined)
        .filter((cuisine, index, self) => self.indexOf(cuisine) === index)

      const priceRanges = restaurants.map(
        (dish) => dish?.restaurantDTO?.priceRange,
      )
      const uniquePriceRanges = Array.from(new Set(priceRanges))

      if (uniquePriceRanges.length > 1) {
        filterItems.push({
          key: 2,
          name: 'Price',

          items: Array.from({ length: 4 }).map((_, index) => {
            return {
              key: index + 1,
              name: price[index],
              value: `${index + 1}`,
            }
          }),
          rangeStep: 1,
          filterType: 'range',
          priceType: 'dollar',
        })
      }
      if (uniqueDietaryPreferences.length !== 0) {
        filterItems.push({
          key: 3,
          name: 'Dietary',
          items: uniqueDietaryPreferences.map((item, index) => {
            return { key: index + 1, name: item, value: item }
          }),
          filterType: 'checkbox',
        })
      }
      if (cuisineList.length !== 0) {
        filterItems.push({
          key: 4,
          name: 'Cuisine',
          items: cuisineList.map((item, index) => {
            return { key: index + 1, name: item, value: item }
          }),
          filterType: 'checkbox',
        })
      }
    }

    return filterItems
  }

  const formatQueryName = (query: string): string =>
    query.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

  // Tappable popular-cuisine chips — shown in BOTH the entry state (no query yet) and the
  // no-results state (a query that matched nothing), so the user always has a way forward.
  const cuisineChips = (
    <>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}
      >
        Popular cuisines
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'center',
          maxWidth: 420,
          mx: 'auto',
        }}
      >
        {POPULAR_CUISINES.map((cuisine) => (
          <Chip
            key={cuisine}
            label={cuisine}
            clickable
            onClick={() =>
              navigate(
                `${ROUTES.SEARCH}?type=cuisine&q=${encodeURIComponent(
                  cuisine.toLowerCase(),
                )}`,
              )
            }
            sx={{ borderRadius: '999px' }}
          />
        ))}
      </Box>
    </>
  )

  // Landing on the results page with no query yet (e.g. the bottom-nav Search tab routes to
  // /search with no ?q=) is NOT a failed search — it's the entry state. Show a neutral prompt to
  // start searching instead of the "No Results Found" error copy, which read as a broken screen.
  if (!query) {
    return (
      <SearchResultsMainContainer>
        <Helmet>
          <title>Resactly | Search restaurants and dishes</title>
          <meta
            name="description"
            content="Search Resactly to discover restaurants and dishes near you. Find your next favorite spot by name, cuisine, or dish."
          />
        </Helmet>
        <EmptyState
          containerHeight={'600px'}
          mainText="Search for restaurants and dishes"
          secondaryText="Find your next favorite spot — search by name, cuisine, or dish using the search bar above."
        >
          {cuisineChips}
        </EmptyState>
      </SearchResultsMainContainer>
    )
  }

  // No-results state for a free-text search (cuisine/city searches route results through Redux, not
  // the local restaurants/dishes state, so scope this to text search). Once the load has settled
  // (searchTerm set, not loading) with zero matches, offer the popular-cuisine chips as a way out
  // instead of a bare "no results" screen.
  const isTextSearch = !type || type === 'restaurant'
  if (
    isTextSearch &&
    searchTerm &&
    !loadingMore &&
    restaurants.length === 0 &&
    dishes.length === 0
  ) {
    return (
      <SearchResultsMainContainer>
        <Helmet>
          <title>Resactly | No results for {query}</title>
        </Helmet>
        <EmptyState
          containerHeight={'600px'}
          mainText={`No matches for “${query}”`}
          secondaryText="Try a different spelling or a nearby place — or explore a popular cuisine:"
        >
          {cuisineChips}
        </EmptyState>
      </SearchResultsMainContainer>
    )
  }

  return (
    <SearchResultsMainContainer>
      <Helmet>
        <title>{pagTitle}</title>
        <meta
          name="description"
          content="Use Resactly’s advanced search functionality to discover restaurants that match your criteria. Filter by location, cuisine, and more to find the perfect dining spot for any occasion."
        />
      </Helmet>
      <SearchResultsHeaderContainer>
        <SearchResultsHeaderLeftContainer>
          <SearchResultsHeaderSearchTermTypography>
            {searchTerm}
          </SearchResultsHeaderSearchTermTypography>
        </SearchResultsHeaderLeftContainer>
        <SearchResultsHeaderRightContainer>
          {isMediumScreen ? (
            <RoundedButton
              component="p"
              borderColor={theme.palette.grey[800]}
              diameter={37}
              onClick={() => setFilterModalOpen(true)}
            >
              <IconAdjustments
                size={18}
                color={theme.palette.grey[800]}
                stroke={2}
              />
            </RoundedButton>
          ) : (
            <>
              <div>
                <Filter
                  onFilterData={onFilter}
                  tabList={filterTabList}
                  rowData={restaurants}
                  isFilterModalOpen={filterModalOpen}
                  setFilterModalOpen={setFilterModalOpen}
                />
              </div>
              <SearchResultsFilterButton
                startIcon={<IconAdjustments size={18} stroke={2} />}
                onClick={() => setFilterModalOpen(true)}
              >
                Filter
              </SearchResultsFilterButton>
            </>
          )}
        </SearchResultsHeaderRightContainer>
      </SearchResultsHeaderContainer>
      {broadened && (type === 'cuisine' || type === 'city') && (
        <Box
          sx={{
            mt: '4px',
            mb: '8px',
            px: '12px',
            py: '10px',
            borderRadius: '10px',
            border: `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.palette.grey[700], fontSize: '13.5px' }}
          >
            No {query} spots in your area yet — showing the nearest available.
          </Typography>
        </Box>
      )}
      {isMediumScreen && (
        <Box mt="12px">
          <Filter
            onFilterData={onFilter}
            tabList={filterTabList}
            rowData={restaurants}
            isFilterModalOpen={filterModalOpen}
            setFilterModalOpen={setFilterModalOpen}
          />
        </Box>
      )}

      <SearchResultsResactlyTabContainer>
        <ResactlyTab
          list={tabList}
          tabComponent={[
            <>
              <SearchResultRestaurants
                restaurantList={filterRestaurants}
                term={query ?? searchTerm}
              />
            </>,
            <>
              <SearchDish
                dishList={dishes}
                searchId={searchId ?? undefined}
                term={query ?? undefined}
              />
            </>,
          ]}
          align="start"
        />
      </SearchResultsResactlyTabContainer>
      {hasMoreSearchResults && (
        <Box display="flex" justifyContent="center" mt="16px" mb="24px">
          <SearchResultsFilterButton
            disabled={loadingMore}
            onClick={() => {
              void loadSearchResults(searchPage + 1, true, locationData)
            }}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </SearchResultsFilterButton>
        </Box>
      )}
    </SearchResultsMainContainer>
  )
}

export default SearchResults
